import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Link,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { forgotPassword } from '../api/auth';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      enqueueSnackbar('Please enter your email address', { variant: 'error' });
      return;
    }

    try {
      console.log('Initiating password reset for email:', email);
      setIsLoading(true);
      
      const response = await forgotPassword(email);
      console.log('Password reset response:', response);
      
      setEmailSent(true);
      enqueueSnackbar('Password reset email sent! Check your inbox.', { 
        variant: 'success',
        autoHideDuration: 5000
      });
    } catch (error) {
      console.error('Password reset error:', error);
      
      let message = 'Error sending reset email';
      if (error.response) {
        // The request was made and the server responded with a status code
        console.error('Server responded with error status:', error.response.status);
        console.error('Error details:', error.response.data);
        
        if (error.response.data && error.response.data.message) {
          message = error.response.data.message;
        } else if (error.response.status === 500) {
          message = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        message = 'Unable to connect to the server. Please check your connection.';
      } else {
        // Something happened in setting up the request
        console.error('Request setup error:', error.message);
        message = `Error: ${error.message}`;
      }
      
      enqueueSnackbar(message, { 
        variant: 'error',
        autoHideDuration: 10000,
        persist: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Check Your Email
          </Typography>
          <Typography paragraph>
            We've sent a password reset link to <strong>{email}</strong>
          </Typography>
          <Typography paragraph>
            Didn't receive the email? Check your spam folder or 
            <Link component="button" onClick={() => setEmailSent(false)} sx={{ ml: 1 }}>
              try again
            </Link>
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          Forgot Your Password?
        </Typography>
        <Typography paragraph align="center">
          Enter your email address and we'll send you a link to reset your password.
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
          </Button>
          
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Back to Login
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;
